import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BzmSearchInput from 'components/forms/BzmSearchInput/BzmSearchInput';
import MultipleSelect, { MultipleSelectItem } from 'components/selectors/MultipleSelect';
import BzmDateRangePicker, {
  DateRange,
  FixedTimeRange,
} from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmDateRangePicker';
import Switcher from 'components/ui/switcher/Switcher';
import {
  lastYearWholeMonthRange,
  minSelectableDate,
  periodToDisplayRange,
  snapRangeToGranularity,
} from 'views/Admin/_shared/dateApi';
import { PeriodPreset, PeriodSelection, UsageFilters, UsageWorkspace } from './types';

interface UsageReportFiltersProps {
  filters: UsageFilters;
  workspaces: UsageWorkspace[];
  onChange: (next: UsageFilters) => void;
}

const SEARCH_DEBOUNCE_MS = 250;

const USAGE_PRESETS: PeriodPreset[] = ['LAST_MONTH', 'LAST_QUARTER', 'LAST_6_MONTHS', 'LAST_YEAR'];

const periodToFixed = (period: PeriodSelection): FixedTimeRange | null =>
  period.kind === 'preset' ? (period.value as FixedTimeRange) : null;

const isUsagePreset = (value: FixedTimeRange): value is PeriodPreset =>
  (USAGE_PRESETS as readonly string[]).includes(value);

const UsageReportFilters: React.FC<UsageReportFiltersProps> = ({ filters, workspaces, onChange }) => {
  const { t } = useTranslation('admin');
  const [searchInput, setSearchInput] = useState(filters.searchTerm);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersRef = useRef(filters);
  const onChangeRef = useRef(onChange);
  filtersRef.current = filters;
  onChangeRef.current = onChange;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput !== filtersRef.current.searchTerm) {
        onChangeRef.current({ ...filtersRef.current, searchTerm: searchInput });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const handleFixedRangeChanged = (preset: FixedTimeRange) => {
    if (!isUsagePreset(preset)) return;
    if (preset === 'LAST_YEAR') {
      // Older than 6 months → monthly granularity: send a whole-month range the backend accepts.
      const range = lastYearWholeMonthRange();
      onChange({ ...filters, period: { kind: 'custom', from: range.from, to: range.to } });
      return;
    }
    onChange({ ...filters, period: { kind: 'preset', value: preset } });
  };

  const handleCustomRangeChanged = (range: DateRange) => {
    // BzmDateRangePicker also fires this on a preset click, with a non-midnight `to` (= now);
    // ignore that echo so a preset is applied once, via fixedSelectionChanged.
    if (range.to.getHours() !== 0 || range.to.getMinutes() !== 0 || range.to.getSeconds() !== 0) {
      return;
    }
    const snapped = snapRangeToGranularity(range);
    onChange({ ...filters, period: { kind: 'custom', from: snapped.from, to: snapped.to } });
  };

  const presetButtons = [
    { optionValue: 'LAST_MONTH' as FixedTimeRange, label: t('usageReport.lastMonth') },
    { optionValue: 'LAST_QUARTER' as FixedTimeRange, label: t('usageReport.lastQuarter') },
    { optionValue: 'LAST_6_MONTHS' as FixedTimeRange, label: t('usageReport.last6Months') },
    { optionValue: 'LAST_YEAR' as FixedTimeRange, label: t('usageReport.lastYear') },
  ];

  const workspaceItems: MultipleSelectItem[] = workspaces.map((w: UsageWorkspace) => ({
    value: String(w.id),
    label: w.name,
  }));

  const selectedWorkspaceItems: MultipleSelectItem[] = workspaceItems.filter(item =>
    filters.workspaceIds.includes(Number(item.value)),
  );

  const handleWorkspaceChange = (selected: MultipleSelectItem[] | MultipleSelectItem) => {
    const items = selected == null ? [] : ([] as MultipleSelectItem[]).concat(selected as MultipleSelectItem[]);
    onChange({ ...filters, workspaceIds: items.map(item => Number(item.value)) });
  };

  const handleIncludeDeletedChange = () => {
    onChange({ ...filters, includeDeletedMocks: !filters.includeDeletedMocks });
  };

  const displayRange: DateRange = useMemo(() => periodToDisplayRange(filters.period), [filters.period]);

  return (
    <div className="usage-report-filters">
      <div className="usage-report-filters__field">
        <label className="usage-report-filters__field-label">{t('usageReport.timeFrameLabel')}</label>
        <BzmDateRangePicker
          presets={presetButtons}
          presetsInPopover={true}
          showTimePicker={false}
          isCompact={true}
          fixedSelection={periodToFixed(filters.period)}
          dateRange={displayRange}
          fixedSelectionChanged={handleFixedRangeChanged}
          dateRangeChanged={handleCustomRangeChanged}
          minSelectableDate={minSelectableDate()}
        />
      </div>

      <div className="usage-report-filters__field">
        <label className="usage-report-filters__field-label">{t('usageReport.workspaceLabel')}</label>
        <div className="usage-report-filters__workspace">
          <MultipleSelect
            items={workspaceItems}
            selected={selectedWorkspaceItems}
            onChange={handleWorkspaceChange}
            placeholder={t('usageReport.allWorkspaces')}
            isMulti={true}
          />
        </div>
      </div>

      <div className="usage-report-filters__field usage-report-filters__deleted-toggle">
        <Switcher
          className={'mt-2'}
          checked={filters.includeDeletedMocks}
          onChange={handleIncludeDeletedChange}
          size="sm"
          label={t('usageReport.includeDeletedLabel')}
        />
      </div>

      <div className="usage-report-filters__field usage-report-filters__search">
        <BzmSearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('usageReport.searchPlaceholder')}
        />
      </div>
    </div>
  );
};

export default UsageReportFilters;
